import { useEffect, useMemo, useState } from "react";
import { theme, FONT_RUNE } from "../theme";
import {
  applyTurn,
  createInitialState,
  defaultVictoryConfig,
  toAlgebraic,
  type GameState,
  type VictoryConfig,
  type VictoryLevel,
} from "../../engine";
import { getGameWithMoves, type GameMoveRecord, type GameSummary } from "../../games/api";
import { ReplayBoard } from "./ReplayBoard";
import * as S from "./sheet";

type Status =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ok"; game: GameSummary; moves: GameMoveRecord[] };

const VALID_LEVELS: VictoryLevel[] = ["common", "proper", "greater", "royal", "glorious"];

function isValidLevel(v: string): v is VictoryLevel {
  return (VALID_LEVELS as string[]).includes(v);
}

/**
 * Move-by-move replay of a stored game. We never call into the live store — instead we
 * recreate the engine state from the move log via applyTurn, exactly like the original game
 * did when it played out. Stepping forward applies one more move; stepping back rewinds by
 * replaying from the initial state (cheap — board is small).
 */
export function GameReplayScreen({ gameId, onClose }: { gameId: string; onClose: () => void }) {
  const [status, setStatus] = useState<Status>({ kind: "loading" });
  const [turnIndex, setTurnIndex] = useState(0);

  useEffect(() => {
    void (async () => {
      const result = await getGameWithMoves(gameId);
      if (!result) {
        setStatus({ kind: "error", message: "Couldn't load this game." });
        return;
      }
      setStatus({ kind: "ok", game: result.game, moves: result.moves });
      setTurnIndex(result.moves.length); // start at the end so the user sees the final position first
    })();
  }, [gameId]);

  const state = useMemo<GameState | null>(() => {
    if (status.kind !== "ok") return null;
    const level: VictoryLevel = isValidLevel(status.game.victoryLevel) ? status.game.victoryLevel : "common";
    const victoryConfig: VictoryConfig = {
      ...defaultVictoryConfig(),
      level,
      commonThreshold: status.game.threshold ?? defaultVictoryConfig().commonThreshold,
    };
    let st = createInitialState(victoryConfig);
    for (let i = 0; i < turnIndex; i++) {
      const m = status.moves[i];
      try {
        st = applyTurn(st, m.move, { declared: m.declared ?? undefined, advance: m.advance }).state;
      } catch (e) {
        // The stored move log shouldn't fail to replay, but if it does we stop where we are
        // rather than crashing the viewer.
        console.warn("replay: applyTurn failed at turn", i + 1, e);
        break;
      }
    }
    return st;
  }, [status, turnIndex]);

  if (status.kind === "loading") {
    return (
      <FrameWrap onClose={onClose} title="Loading replay…">
        <div style={{ padding: 24, color: theme.parchment, opacity: 0.8 }}>Loading…</div>
      </FrameWrap>
    );
  }
  if (status.kind === "error") {
    return (
      <FrameWrap onClose={onClose} title="Replay">
        <div style={{ padding: 24, color: theme.captureFlash }}>{status.message}</div>
      </FrameWrap>
    );
  }
  if (!state) return null;

  const totalTurns = status.moves.length;
  const lastMove = turnIndex > 0 ? status.moves[turnIndex - 1] : null;
  const ended = !!state.victory && turnIndex === totalTurns;

  return (
    <div style={S.overlay} onClick={onClose}>
      <div
        style={{
          ...S.card,
          maxWidth: 540,
          maxHeight: "calc(100dvh - 32px)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={S.header}>
          <span style={S.title}>Game replay</span>
          <button onClick={onClose} style={S.closeButton} aria-label="Close">×</button>
        </div>

        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            padding: "12px 16px 16px",
            // Container queries let ReplayBoard cap its width by the available height — same
            // trick the live BoardCanvas uses, so the board keeps its 1:2 shape.
            containerType: "size",
            gap: 12,
          }}
        >
          <div style={{ flex: 1, display: "flex", justifyContent: "center", minHeight: 0 }}>
            <ReplayBoard state={state} />
          </div>

          <MoveCaption lastMove={lastMove} turnIndex={turnIndex} totalTurns={totalTurns} ended={ended} victorySide={state.victory?.side ?? null} victoryDetail={state.victory?.detail ?? null} />

          <div style={controlsRow}>
            <button onClick={() => setTurnIndex(0)} disabled={turnIndex === 0} style={ctrlButton} title="Start">
              ⏮
            </button>
            <button
              onClick={() => setTurnIndex((t) => Math.max(0, t - 1))}
              disabled={turnIndex === 0}
              style={ctrlButton}
              title="Previous"
            >
              ←
            </button>
            <span style={turnLabel}>
              {turnIndex} / {totalTurns}
            </span>
            <button
              onClick={() => setTurnIndex((t) => Math.min(totalTurns, t + 1))}
              disabled={turnIndex >= totalTurns}
              style={ctrlButton}
              title="Next"
            >
              →
            </button>
            <button
              onClick={() => setTurnIndex(totalTurns)}
              disabled={turnIndex >= totalTurns}
              style={ctrlButton}
              title="End"
            >
              ⏭
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FrameWrap({ children, onClose, title }: { children: React.ReactNode; onClose: () => void; title: string }) {
  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={{ ...S.card, maxWidth: 540 }} onClick={(e) => e.stopPropagation()}>
        <div style={S.header}>
          <span style={S.title}>{title}</span>
          <button onClick={onClose} style={S.closeButton} aria-label="Close">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function MoveCaption({
  lastMove,
  turnIndex,
  totalTurns,
  ended,
  victorySide,
  victoryDetail,
}: {
  lastMove: GameMoveRecord | null;
  turnIndex: number;
  totalTurns: number;
  ended: boolean;
  victorySide: "white" | "black" | null;
  victoryDetail: string | null;
}) {
  if (turnIndex === 0) {
    return (
      <div style={captionBox}>
        <span style={{ color: theme.boardLabel, fontSize: 12, fontFamily: FONT_RUNE, letterSpacing: 1 }}>STARTING POSITION</span>
        <span style={{ fontSize: 13, color: theme.parchment, opacity: 0.85 }}>
          Step forward with → to walk the game move by move.
        </span>
      </div>
    );
  }

  if (!lastMove) return null;

  const declared = lastMove.declared;
  const moveLine = `${turnIndex} of ${totalTurns} · ${capitalize(lastMove.side)} ${toAlgebraic(lastMove.move.from)} → ${toAlgebraic(lastMove.move.to)}`;
  const captureLine = declared
    ? `${declared.method.toUpperCase()} · ${declared.formula}${lastMove.advance ? " (advanced)" : ""}`
    : null;

  return (
    <div style={captionBox}>
      <span style={{ color: theme.boardLabel, fontSize: 12, fontFamily: FONT_RUNE, letterSpacing: 1 }}>{moveLine}</span>
      {captureLine && (
        <span style={{ fontSize: 13, color: theme.accentSoft, fontFamily: FONT_RUNE }}>{captureLine}</span>
      )}
      {ended && victorySide && (
        <span style={{ fontSize: 13, color: theme.parchment, opacity: 0.92 }}>
          <b>{capitalize(victorySide)} wins.</b>
          {victoryDetail ? ` ${victoryDetail}` : ""}
        </span>
      )}
    </div>
  );
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const controlsRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
};

const ctrlButton: React.CSSProperties = {
  width: 44,
  height: 44,
  fontFamily: FONT_RUNE,
  fontSize: 18,
  background: theme.boardEdge,
  color: theme.parchment,
  border: `1px solid ${theme.inkSoft}`,
  borderRadius: 6,
  cursor: "pointer",
};

const turnLabel: React.CSSProperties = {
  minWidth: 84,
  textAlign: "center",
  fontFamily: FONT_RUNE,
  fontSize: 13,
  letterSpacing: 1,
  color: theme.parchment,
};

const captionBox: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  padding: "10px 12px",
  background: theme.bg,
  borderRadius: 6,
  border: `1px solid ${theme.boardEdge}`,
};
